import { PaginationParamType } from "type";
import apiClient from "config/apiClient";
import { PriceSettingMethod } from "features/proc_fee/types/tire";
import { BrandData, UpdateWheelPriceRequest, StorePriceSettingWheelParams, StorePriceSettingWinterWheelParams } from "features/mst_wheel/types/wheel-price";
import {
  SelectWinterWheelPriceRequest,
  UpdateWinterWheelPriceRequest,
  WinterWheelPriceItem,
  WinterWheelSellingItem,
} from "features/mst_wheel/types/winter-wheel-price";

type ResponseData<T> = {
  list: Array<T>;
  total: number;
};

const WHEEL_PRICE_LIST_URL = "/mst/item/brand/detail/wheel/select";
const UPDATE_WHEEL_PRICE_URL = "/mst/item/brand/detail/wheel/update";
const WINTER_WHEEL_PRICE_LIST_URL = "/mst/winter/wheel/selling/price/select";
const UPDATE_WINTER_WHEEL_PRICE_URL = "/mst/winter/wheel/selling/price/update";
const SELECT_WINTER_WHEEL_PRICE_URL = "/mst/winter/wheel/selling/price/wheel/list/select";
const SELECT_WINTER_WHEEL_PRICE_METHOD_URL = "/mst/store/select";
const UPDATE_STORE_PRICE_SETTING_URL = "/mst/store/update";

/**
 * Fetches the wheel price list with pagination.
 * @param params - Pagination and filter parameters.
 * @returns A promise resolving to the response data containing a list of brand data and total count.
 */
export const fetchWheelPrice = async (params: PaginationParamType) => {
  const response = await apiClient.post<ResponseData<BrandData>>(WHEEL_PRICE_LIST_URL, params);
  return response.data;
};

/**
 * Updates the wheel price with the provided data.
 * @param data - The request payload containing updated wheel price information.
 * @returns A promise resolving to the API response.
 */
export const updateWheelPrice = async (data: UpdateWheelPriceRequest) => {
  const response = await apiClient.post(UPDATE_WHEEL_PRICE_URL, data);
  return response;
};

/**
 * Fetches the winter wheel price list with pagination.
 * @param params - Pagination and filter parameters.
 * @returns A promise resolving to the response data containing a list of winter wheel price items and total count.
 */
export const fetchWinterWheelPrice = async (params: PaginationParamType) => {
  const response = await apiClient.post<ResponseData<WinterWheelPriceItem>>(
    WINTER_WHEEL_PRICE_LIST_URL,
    params,
  );
  return response.data;
};

/**
 * Updates the winter wheel price with the provided data.
 * @param data - The request payload containing updated winter wheel price information.
 * @returns A promise resolving to the API response.
 */
export const updateWinterWheelPrice = async (data: UpdateWinterWheelPriceRequest) => {
  const response = await apiClient.post(UPDATE_WINTER_WHEEL_PRICE_URL, data);
  return response.data;
};

/**
 * Fetches the selectable winter wheel price list.
 * @param data - The request payload for selecting winter wheel prices.
 * @returns A promise resolving to the API response data.
 */
export const fetchSelectWinterWheelPrice = async (data: SelectWinterWheelPriceRequest | {}) => {
  const response = await apiClient.post<ResponseData<WinterWheelSellingItem>>(
    SELECT_WINTER_WHEEL_PRICE_URL,
    data,
  );
  return response.data;
};

/**
 * Fetches the winter wheel price method with the provided data.
 * @param data - The request payload for selecting winter wheel price method.
 * @returns A promise resolving to the API response data.
 */
export const selectWinterWheelPriceMethod = async (data: { store_cd: string }):Promise<PriceSettingMethod> => {
  const response = await apiClient.post(SELECT_WINTER_WHEEL_PRICE_METHOD_URL, data);
  return response.data as PriceSettingMethod;
};

/**
 * Updates the store price setting (wheel/winter wheel) with the provided data.
 * @param data - The request payload containing updated wheel store price setting information.
 * @returns A promise resolving when the update is complete.
 */
export const updateStorePriceSettingApi = async (data: StorePriceSettingWheelParams | StorePriceSettingWinterWheelParams): Promise<void> => {
  await apiClient.post(UPDATE_STORE_PRICE_SETTING_URL, data);
};
